/**
 * Detection of, and recovery from, a build older than the deployment's minimum version.
 *
 * Each build publishes /version.json (scripts/forceUpdate.js) holding the deployed build and the
 * oldest build it still supports, taken from min-version.json.
 */

export interface DeployedVersion {
  version: string
  minVersion: string
}

/** The build this page is running, stamped by scripts/stampVersion.js. Empty in development. */
export const currentVersion: string = process.env.REACT_APP_BUILD_VERSION || ''

/** "2026-09-14 20:25 (a68d98858)" -> "2026-09-14 20:25". Minimums may leave the time out. */
const comparable = (version: string) =>
  /^\d{4}-\d{2}-\d{2}(?: \d{2}:\d{2})?/.exec(version || '')?.[0] || ''

export const isBelowMinimum = (version: string, minVersion: string) => {
  const current = comparable(version)
  const minimum = comparable(minVersion)
  return !!current && !!minimum && current < minimum
}

/** Null when the file can't be had — offline, or a deployment from before version.json existed. */
export const fetchDeployedVersion = async (): Promise<DeployedVersion | null> => {
  try {
    // version.json is written after the build, so the service worker never precaches it; the query
    // string and no-store keep the HTTP cache out of the way too.
    const res = await fetch(`${process.env.PUBLIC_URL}/version.json?t=${Date.now()}`, {
      cache: 'no-store',
    })
    if (!res.ok) {
      return null
    }
    const body = await res.json()
    return typeof body?.version === 'string' && typeof body?.minVersion === 'string' ? body : null
  } catch (err) {
    return null
  }
}

/** Asks the browser to look for a newer service worker now, instead of at the next navigation. */
export const checkForServiceWorkerUpdate = () => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .getRegistration()
      .then((registration) => registration?.update())
      .catch(() => undefined)
  }
}

/*
 * Reloads count per tab and per minimum, so a browser that keeps coming back on the old build gets
 * instructions instead of an endless reload loop.
 */
const ATTEMPTS_KEY = 'forceUpdateAttempts'
export const MAX_AUTOMATIC_ATTEMPTS = 2

export const automaticAttempts = (minVersion: string): number => {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(ATTEMPTS_KEY) || 'null')
    return stored?.minVersion === minVersion ? stored.count : 0
  } catch (err) {
    return 0
  }
}

export const clearAutomaticAttempts = () => {
  try {
    window.sessionStorage.removeItem(ATTEMPTS_KEY)
  } catch (err) {
    // Storage unavailable: nothing was recorded either.
  }
}

const recordAttempt = (minVersion: string) => {
  try {
    window.sessionStorage.setItem(
      ATTEMPTS_KEY,
      JSON.stringify({ minVersion, count: automaticAttempts(minVersion) + 1 }),
    )
  } catch (err) {
    // Without storage the loop guard can't work, but updating still beats staying on the old build.
  }
}

const CLEANUP_TIMEOUT_MS = 10000

/**
 * Drops everything the browser kept from the old build, then reloads from the server.
 *
 * Unregistering the service worker is what makes the reload count: while the old worker controls the
 * site it answers the reload with the old index.html, and a new worker waits until every tab closes.
 * The page registers a fresh worker once the new build is up.
 */
export const reloadIntoLatestVersion = async (minVersion: string) => {
  recordAttempt(minVersion)

  const cleanup = async () => {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map((registration) => registration.unregister()))
    }
    if ('caches' in window) {
      const names = await caches.keys()
      await Promise.all(names.map((name) => caches.delete(name)))
    }
  }

  try {
    await Promise.race([
      cleanup(),
      new Promise((resolve) => window.setTimeout(resolve, CLEANUP_TIMEOUT_MS)),
    ])
  } catch (err) {
    console.error('Force update: could not clear the old version', err)
  }

  window.location.reload()
}
